import React, { type JSX } from "react";
import { getInvoiceHighlightConfig } from "../../../utils/utils";
import {
  AmplifyCodeSourceHeader,
  AmplifyInvoiceSourceHeader,
  ArtifactTypes,
  InvoiceTypes
} from '../../artifacts/artifact-types.lang';
import {
  CodeSources,
  GenericBaseCodeChip,
  ProcedureAndTechCode
} from './code-sources.tsx/code-sources';
import classNames from 'classnames';
import { CMXIcon } from '@codametrix/ui-components';
import './styles/index.scss';

type InvoiceType = keyof typeof InvoiceTypes;


const INVOICES_SORT_ORDER: Record<InvoiceType, number> = {
  CLINICAL: 1,
  MAPPED: 2,
  PREDICTION: 3,
  PREDICTION_COMPONENT: 4,
  ASSEMBLED: 5,
  CODED: 6,
  PROCESSED: 7,
  SUBMITTED: 8,
};




export function buildInvoiceSourceHeader(invoiceType: string) {
  if (!invoiceType) {
    return '';
  }

  if (invoiceType in AmplifyInvoiceSourceHeader) {
    return AmplifyInvoiceSourceHeader[invoiceType];
  } else if (invoiceType in ArtifactTypes) {
    return ArtifactTypes[invoiceType];
  }

  return `From ${invoiceType[0].toUpperCase() +
    invoiceType.slice(1).toLowerCase()}`;
}

export function buildCodeSourceHeader(
  invoiceType: string,
  sequenceNumber: number
) {
  if (!invoiceType) {
    return '';
  }

  if (invoiceType === InvoiceTypes.ASSEMBLED) {
    return `Transaction ${sequenceNumber}`;
  } else if (invoiceType in AmplifyCodeSourceHeader) {
    return AmplifyCodeSourceHeader[invoiceType];
  }

  return '';
}

export interface GenericTransaction {
  id: string;
  diagnoses: GenericBaseCodeChip[] | null;
  modifiers: GenericBaseCodeChip[] | null;
  procedures: ProcedureAndTechCode[] | null;
}

export interface GenericInvoice {
  id: string;
  type: string;
  performingProviderName: string;
  transactions: GenericTransaction[];
  ledgerTransactions?: CMxCommonApp.CaseLedgerTransactionDto[];
}

interface Props {
  invoices: GenericInvoice[];
}

export function InvoiceSources({ invoices }: Props): JSX.Element {
  if (!invoices || invoices.length === 0) {
    return <span>No code sources available.</span>;
  }
  // Normalize invoice types to uppercase for consistent sorting and header display
  const normalizedInvoices = invoices.map(inv => ({
    ...inv,
    type: inv.type?.toUpperCase(),
  }));

  const invoicesSorted = [...normalizedInvoices].sort(
    (inv1, inv2) =>
      INVOICES_SORT_ORDER[inv1.type] - INVOICES_SORT_ORDER[inv2.type]
  );
    
  return (
    <div className="invoice-sources-tab">
      
      {invoicesSorted.map(invoice => {
        const highlightConfig = getInvoiceHighlightConfig(invoice.type,invoice.ledgerTransactions);
        return (
          <div
  className={`invoice-source ${highlightConfig?.className || ''}`}
  key={invoice.id}
  id={invoice.id}
>
            <div className="invoice-type-container">
              <div className="invoice-type">
                {buildInvoiceSourceHeader(invoice.type)}
              </div>
              {invoice.type === InvoiceTypes.CLINICAL && (
                <div className="clinical-source-provider">
                  {invoice.performingProviderName}
                </div>
              )}
            </div>
            
            {highlightConfig && (
              <div className="code-source-message">
                
                <div className="code-source-message-icon">
                  <CMXIcon
                    svgIconName="BiomeInfo"
                    svgIconClass={classNames("code-source-icon")}
                    svgIconSize="20"
                  />
                </div>

                <div className="code-source-message-text">
                  {highlightConfig.message}
                </div>

              </div>
            )}
            {invoice.transactions.map((transaction, index) => {
              return (
                <CodeSources
                  key={transaction.id}
                  title={buildCodeSourceHeader(invoice.type, index + 1)}
                  diagnoses={transaction.diagnoses}
                  modifiers={transaction.modifiers}
                  procedures={transaction.procedures}
                />
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
